import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../data/site";
import { inputCls, Field, DropZone, AdminButton } from "../ui";

const FIELDS = [
  { k: "business_name", label: "Business Name" },
  { k: "phone", label: "Phone Number" },
  { k: "email", label: "Public Email Address" },
  { k: "contact_email", label: "Contact Form Email Destination (where inquiries are sent)" },
  { k: "instagram", label: "Instagram URL" },
  { k: "instagram_handle", label: "Instagram Handle" },
  { k: "facebook", label: "Facebook URL" },
  { k: "youtube", label: "YouTube URL" },
  { k: "google_review_url", label: "Google Review Link (from Google Business Profile → 'Ask for reviews')" },
  { k: "address", label: "Business Address" },
  { k: "hours", label: "Office Hours" },
  { k: "region", label: "Service Region Line (e.g. Idaho's Treasure Valley)" },
  { k: "footer_text", label: "Footer Text (optional)", multiline: true },
  { k: "default_cta_label", label: "Default Button Text" },
  { k: "default_cta_link", label: "Default Button Link" },
];

const SettingsPanel = () => {
  const { data, refetch } = useQuery({
    queryKey: ["settings-admin"],
    queryFn: async () => (await axios.get(`${API}/settings`, { withCredentials: true })).data,
  });
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (data) setForm(data); }, [data]);

  const save = async () => {
    if (!window.confirm("Update website settings? Phone, email, and social links change everywhere on the live site.")) return;
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, form, { withCredentials: true });
      toast.success("Settings saved — live across the website");
      refetch();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="settings-panel">
      <p className="text-sm text-[#5C5A56] max-w-2xl mb-8">
        These details appear across the whole website — header, footer, contact page, and buttons. Change them here once and they update everywhere.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl">
        {FIELDS.map((f) => (
          <Field key={f.k} label={f.label} span={f.multiline ? 2 : 1} testid={`settings-field-${f.k}`}>
            {f.multiline ? (
              <textarea rows={2} value={form[f.k] || ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} className={inputCls} data-testid={`settings-${f.k}`} />
            ) : (
              <input value={form[f.k] || ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} className={inputCls} data-testid={`settings-${f.k}`} />
            )}
          </Field>
        ))}
        <Field label="Mark's Profile Photo (About page)" span={2}>
          <input value={form.builder_photo || ""} onChange={(e) => setForm({ ...form, builder_photo: e.target.value })} className={inputCls} placeholder="Image URL — or drop a photo below" data-testid="settings-builder-photo" />
          <DropZone onUploaded={(url) => setForm((p) => ({ ...p, builder_photo: url }))} accept="image/*" multiple={false} category="About / Mark" label="Drop Mark's photo here" testid="settings-builder-photo-dropzone" />
        </Field>
        <Field label="Logo" span={2}>
          <input value={form.logo_url || ""} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} className={inputCls} placeholder="Leave blank to use the built-in logo" data-testid="settings-logo" />
          <DropZone onUploaded={(url) => setForm((p) => ({ ...p, logo_url: url }))} accept="image/*" multiple={false} category="Homepage" label="Drop a new logo file here" testid="settings-logo-dropzone" />
        </Field>
        <Field label="Favicon (browser tab icon)" span={2}>
          <input value={form.favicon_url || ""} onChange={(e) => setForm({ ...form, favicon_url: e.target.value })} className={inputCls} placeholder="Leave blank to use the built-in icon" data-testid="settings-favicon" />
        </Field>
        <Field label="Default Social Share Image" span={2}>
          <input value={form.share_image || ""} onChange={(e) => setForm({ ...form, share_image: e.target.value })} className={inputCls} placeholder="Image URL shown when the site is shared" data-testid="settings-share-image" />
          <DropZone onUploaded={(url) => setForm((p) => ({ ...p, share_image: url }))} accept="image/*" multiple={false} category="Homepage" label="Drop a share image here" testid="settings-share-dropzone" />
        </Field>
      </div>
      <div className="mt-8 flex gap-3">
        <AdminButton primary type="button" onClick={save} disabled={saving} testid="settings-save">{saving ? "Saving…" : "Save Settings"}</AdminButton>
      </div>
    </div>
  );
};

export default SettingsPanel;
